import React, { useCallback, useEffect, useState } from 'react';
import { FaBookOpen, FaCheckCircle, FaGraduationCap } from 'react-icons/fa';
import {
  checkPublicEducationRegistrationDuplicate,
  createPublicEducationRegistration,
  getPublicEducationFacilities
} from '../../../api/sportsAppointmentService';
import './PublicEducationRegistrationForm.scss';

const PublicEducationRegistrationForm = () => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [successSummary, setSuccessSummary] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    identityNumber: '',
    gender: '',
    birthDate: '',
    phone: '',
    email: '',
    address: ''
  });

  const fetchFacilities = useCallback(async () => {
    const response = await getPublicEducationFacilities();
    if (response.isSuccess) {
      const data = response.data || [];

      const grouped = {};
      data.forEach((facility) => {
        const regionName = facility.regionName || facility.facilityName;
        const regionId = facility.regionId || regionName;

        if (!grouped[regionId]) {
          grouped[regionId] = {
            facilityId: facility.facilityId,
            facilityName: regionName,
            regionId
          };
        }
      });

      setFacilities(Object.values(grouped));
    }
  }, []);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.firstName || !formData.lastName || !formData.identityNumber || !formData.gender || !formData.birthDate || !formData.phone || !formData.email) {
      setError('Lütfen zorunlu alanları doldurunuz.');
      return;
    }

    const normalizedIdentityNumber = formData.identityNumber.replace(/\D/g, '');
    const normalizedPhone = formData.phone.replace(/\D/g, '');
    const normalizedEmail = formData.email.trim().toLowerCase();

    const payload = {
      facilityId: selectedFacilityId ? Number(selectedFacilityId) : null,
      courseScheduleId: null,
      sportTypeId: null,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      identityNumber: normalizedIdentityNumber,
      gender: formData.gender,
      birthDate: formData.birthDate,
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      address: formData.address.trim() || null,
      preferredDate: null,
      preferredStartHour: null,
      preferredEndHour: null,
      notes: null
    };

    setSubmitting(true);
    try {
      const duplicateCheckResponse = await checkPublicEducationRegistrationDuplicate({
        identityNumber: normalizedIdentityNumber,
        phone: normalizedPhone,
        email: normalizedEmail
      });

      if (!duplicateCheckResponse.isSuccess) {
        setError(duplicateCheckResponse.resultMessage || 'Kayıt kontrolü yapılamadı.');
        return;
      }

      const duplicateData = duplicateCheckResponse.data || {};
      const identityNumberExists = Boolean(duplicateData.identityNumberExists ?? duplicateData.IdentityNumberExists);
      const phoneExists = Boolean(duplicateData.phoneExists ?? duplicateData.PhoneExists);
      const emailExists = Boolean(duplicateData.emailExists ?? duplicateData.EmailExists);

      if (identityNumberExists || phoneExists || emailExists) {
        const duplicateFields = [];
        if (identityNumberExists) duplicateFields.push('TC Kimlik No');
        if (phoneExists) duplicateFields.push('telefon');
        if (emailExists) duplicateFields.push('e-posta');

        const duplicateMessage =
          duplicateFields.length === 1
            ? `Bu ${duplicateFields[0]} ile daha önce başvuru yapilmi?Y.`
            : `Bu bilgilerle daha önce başvuru yapılmış (${duplicateFields.join(', ')}).`;

        setError(duplicateMessage);
        return;
      }

      const response = await createPublicEducationRegistration(payload);
      if (response.isSuccess) {
        const facility = facilities.find(f => String(f.facilityId) === String(selectedFacilityId));
        setSuccessSummary({
          ...formData,
          facilityName: facility ? facility.facilityName : 'Bilinmeyen Tesis'
        });
        setSuccessMessage('Başvurunuz alınmıştır. Halk Eğitim panelinde görünecektir.');
        setFormData({
          firstName: '',
          lastName: '',
          identityNumber: '',
          gender: '',
          birthDate: '',
          phone: '',
          email: '',
          address: ''
        });
        setSelectedFacilityId('');
        setResetKey(prev => prev + 1);
      } else {
        setError(response.resultMessage || 'Başvuru kaydedilemedi.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sa-panel-inner sa-panel-inner--wide sa-public-education sa-unified-mode">
      <div className="sa-facility-details">
        <div className="sa-facility-details-header sa-unified-tab-header">
          <div className="sa-unified-tab-header-row">
            <h3 className="sa-unified-tab-title">
              <div className="sa-unified-tab-icon">
                <FaGraduationCap size={20} />
              </div>
              Halk Eğitim Kayıt Formu
            </h3>
          </div>
          <p className="sa-unified-tab-subtitle">
            Lütfen başvuru yapmak istediğiniz tesisi seçip bilgilerinizi giriniz.
          </p>
        </div>

        {successSummary ? (
          <div className="sa-appointment-section sa-public-section">
            <div className="sa-payment-result sa-payment-success" style={{ textAlign: 'center', padding: '30px' }}>
              <div className="sa-result-icon">
                <FaCheckCircle style={{ fontSize: '48px', color: '#16a34a', marginBottom: '15px' }} />
              </div>
              <h2>Başvuru Alındı</h2>
              <p className="sa-result-message">{successMessage}</p>

              <div className="sa-checkout-summary" style={{ marginTop: '20px', textAlign: 'left', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #cbd5e1', paddingBottom: '10px' }}>Başvuru Özeti</h4>
                <div style={{ display: 'grid', gap: '10px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Ad Soyad:</span>
                    <strong style={{ color: '#1e293b' }}>{successSummary.firstName} {successSummary.lastName}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>TC Kimlik No:</span>
                    <strong style={{ color: '#1e293b' }}>{successSummary.identityNumber}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Telefon:</span>
                    <strong style={{ color: '#1e293b' }}>{successSummary.phone}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Tesis:</span>
                    <strong style={{ color: '#1e293b' }}>{successSummary.facilityName}</strong>
                  </div>
                </div>
              </div>

              <div className="sa-action-row sa-public-action-row" style={{ marginTop: '20px', justifyContent: 'center' }}>
                <button type="button" className="sa-btn-cancel" onClick={() => { setSuccessSummary(null); setSuccessMessage(''); }}>
                  Yeni Başvuru
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form className="sa-appointment-section sa-public-section" onSubmit={handleSubmit}>
            <div className="sa-booking-form sa-public-booking-form sa-public-booking-form--facility" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none' }}>
              <div className="sa-form-grid">
                <div className="sa-form-group">
                  <label>Tesis <span className="sa-required">*</span></label>
                  <select
                    className="sa-form-input"
                    value={selectedFacilityId}
                    onChange={(event) => setSelectedFacilityId(event.target.value)}
                    required
                  >
                    <option value="">Tesis seçiniz</option>
                    {facilities.map((facility) => (
                      <option key={facility.facilityId} value={facility.facilityId}>
                        {facility.facilityName}
                      </option>
                    ))}
                  </select>
                  {!facilities.length && <small className="sa-public-empty-facility">Aktif tesis bulunamadı.</small>}
                </div>
              </div>
            </div>

            <div className="sa-booking-form sa-public-booking-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none' }}>
              <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                Kişisel Bilgiler
              </h4>
              <div className="sa-form-grid">
                <div className="sa-form-group">
                  <label>Ad <span className="sa-required">*</span></label>
                  <input className="sa-form-input" value={formData.firstName} onChange={(event) => setFormData({ ...formData, firstName: event.target.value })} required />
                </div>
                <div className="sa-form-group">
                  <label>Soyad <span className="sa-required">*</span></label>
                  <input className="sa-form-input" value={formData.lastName} onChange={(event) => setFormData({ ...formData, lastName: event.target.value })} required />
                </div>
                <div className="sa-form-group">
                  <label>TC Kimlik No <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    value={formData.identityNumber}
                    maxLength={11}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, '');
                      if (value.length <= 11) setFormData({ ...formData, identityNumber: value });
                    }}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Cinsiyet <span className="sa-required">*</span></label>
                  <select className="sa-form-input" value={formData.gender} onChange={(event) => setFormData({ ...formData, gender: event.target.value })} required>
                    <option value="">Seçiniz</option>
                    <option value="Kadin">Kadın</option>
                    <option value="Erkek">Erkek</option>
                  </select>
                </div>
                  <div className="sa-form-group">
                    <label>Doğum Tarihi <span className="sa-required">*</span></label>
                    <input key={`dob-${resetKey}`} className="sa-form-input" type="date" defaultValue={formData.birthDate} max="9999-12-31" onChange={(event) => setFormData({ ...formData, birthDate: event.target.value })} required />
                  </div>
                <div className="sa-form-group">
                  <label>Telefon <span className="sa-required">*</span></label>
                  <input className="sa-form-input" value={formData.phone} onChange={(event) => setFormData({ ...formData, phone: event.target.value })} required />
                </div>
                <div className="sa-form-group">
                  <label>E-posta <span className="sa-required">*</span></label>
                  <input className="sa-form-input" type="email" value={formData.email} onChange={(event) => setFormData({ ...formData, email: event.target.value })} required />
                </div>
                <div className="sa-form-group">
                  <label>Adres</label>
                  <input className="sa-form-input" value={formData.address} onChange={(event) => setFormData({ ...formData, address: event.target.value })} />
                </div>
              </div>

              {error && <div className="sa-public-message sa-public-message--error">{error}</div>}
              {successMessage && !successSummary && (
                <div className="sa-public-message sa-public-message--success">
                  <FaCheckCircle />
                  {successMessage}
                </div>
              )}

              <div className="sa-action-row sa-public-action-row">
                <button type="submit" className="sa-submit-button" disabled={submitting || !selectedFacilityId}>
                  <FaBookOpen />
                  {submitting ? 'Gönderiliyor...' : 'Halk Eğitim Başvurusu Gönder'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PublicEducationRegistrationForm;
